import { useCallback, useRef, useState } from "react";
import { useIonLoading } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { Camera, CameraErrorCode, MediaTypeSelection } from "@capacitor/camera";
import { correctImageOrientation, imageUrlToJpegFile } from "../services/functions";

/**
 * One way to ask for a photo or a clip, whichever platform we are on.
 *
 * Native goes through the Capacitor Camera plugin, so the member gets the real
 * camera and the system gallery instead of the browser's file chooser; web
 * falls back to a hidden <input>. Both hand back a plain File with the EXIF
 * orientation already baked into the pixels, so callers never have to think
 * about a portrait photo arriving sideways.
 *
 * This hook is the ONLY place in the app that talks to @capacitor/camera. Every
 * screen that needs a picture goes through it — the Storie composer, the comment
 * composer, the post composer, the profile picture and Misure. That is
 * deliberate: the plugin's API turned over in v8 (see below), and when it turns
 * over again there is one file to change rather than five.
 *
 * Uses `takePhoto` / `chooseFromGallery` (@since 8.1.0). The older `getPhoto`
 * and `pickImages` are deprecated in @capacitor/camera 8 and are not used
 * anywhere. `chooseFromGallery` takes a MediaTypeSelection, which is what lets
 * `pickVideoFromGallery` exist at all — the old API returned stills only, so a
 * clip had to detour through a file input.
 *
 * Usage:
 *   const { isNative, pickFromCamera, pickFromGallery, openWebPicker, webInputProps } =
 *     useMediaPicker({ onPicked: (file) => ..., filename: "story.jpg" });
 */

/**
 * Codes the plugin reports when the member simply backed out. Not failures, and
 * they must never surface a message. Checked by code rather than by matching on
 * the message, which is localised and has changed between versions.
 */
const CANCELLED_CODES = new Set([
  CameraErrorCode.TakePhotoCancelled,
  CameraErrorCode.ChooseMediaCancelled,
  CameraErrorCode.EditPhotoCancelled,
]);

const isCancellation = (error) =>
  CANCELLED_CODES.has(error?.code) || /cancel/i.test(error?.message || "");

/**
 * iOS hands back .mov from the photo library, and a blob read out of the iOS
 * WebView often has an empty `type`. Both would reach the API as a file with no
 * usable MIME type, which is what used to fail the upload outright, so the type
 * is recovered from the extension when the blob will not say.
 */
const VIDEO_TYPE_BY_EXTENSION = {
  mov: "video/quicktime",
  qt: "video/quicktime",
  mp4: "video/mp4",
  m4v: "video/mp4",
  webm: "video/webm",
};

const extensionOf = (path) =>
  (String(path).split("?")[0].match(/\.([a-z0-9]{2,5})$/i) || [])[1]?.toLowerCase() || "";

/**
 * XHR rather than fetch. On iOS the plugin hands back a capacitor:// file URL
 * that fetch() answers with an opaque, unreadable response (status 0), while
 * XHR reads it as a blob. Photos never hit this because they are loaded through
 * an <img> and re-encoded on a canvas instead.
 */
const readBlob = (url) =>
  new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("GET", url, true);
    request.responseType = "blob";
    request.onload = () =>
      request.response?.size
        ? resolve(request.response)
        : reject(new Error("Media read returned nothing"));
    request.onerror = () => reject(new Error("Media read failed"));
    request.send();
  });

/**
 * A readable URL -> File, MIME type preserved. Videos must not go anywhere near
 * the canvas re-encode that photos use — it would decode a clip as a still.
 */
const fetchAsFile = async (src, baseName) => {
  const blob = await readBlob(src);
  const extension = extensionOf(src) || "mp4";
  // The extension wins over whatever the blob claims: the local file server
  // often answers "application/octet-stream", which is truthy and used to win,
  // leaving a File that failed every `type.startsWith("video/")` test.
  const claimed = blob.type || "";
  const type =
    VIDEO_TYPE_BY_EXTENSION[extension] ||
    (claimed.startsWith("video/") ? claimed : "video/mp4");
  return new File([blob], `${baseName}.${extension}`, { type });
};

/**
 * The playable URL for a plugin result, whichever field it arrived in.
 *
 * `MediaResult` declares BOTH `uri` and `webPath` optional, and which one you
 * get depends on the call and the platform. Android's `recordVideo` returns a
 * bare filesystem path in `uri` and no `webPath` at all:
 *
 *   {"type":1,"uri":"/data/user/0/it.dietasocial.web/files/…/VID_…mp4", …}
 *
 * Reading `webPath` alone therefore saw `undefined`, treated a successful
 * recording as a cancellation and returned silently — the recorder worked and
 * the Story simply never appeared. `convertFileSrc` turns that path into
 * something the WebView can actually read.
 */
const mediaSrc = (result) => {
  if (!result) return null;
  if (result.webPath) return result.webPath;
  return result.uri ? Capacitor.convertFileSrc(result.uri) : null;
};

/**
 * A clip from the gallery through the WebView's own file chooser, as a File.
 *
 * Used on Android instead of the plugin. After a gallery pick the plugin
 * (ioncamera-android 1.0.2) opens a full-screen "loading" activity and closes it
 * with a broadcast once it has made a thumbnail. For a video that takes very
 * little, and when it is done before the activity has started the broadcast is
 * missed: a spinner then sits over the app, which takes no more taps until the
 * member presses Back — to them, the video "won't post" (client, 03/10/2026).
 * The file chooser has no such screen and hands the file straight back, so it
 * is not read a second time either. Resolves null when the member backs out.
 */
const chooseVideoFile = () =>
  new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "video/*";
    input.style.display = "none";
    const finish = (file) => {
      input.remove();
      resolve(file || null);
    };
    input.addEventListener("change", () => finish(input.files?.[0]));
    input.addEventListener("cancel", () => finish(null));
    document.body.appendChild(input);
    input.click();
  });

/** `chooseFromGallery` resolves { results: [...] }; a cancel can arrive as an empty list. */
const firstResult = (results) =>
  (Array.isArray(results?.results) ? results.results[0] : null) ?? null;

export function useMediaPicker({
  onPicked,
  onError,
  filename = "photo.jpg",
  videoBaseName = "video",
  quality = 90,
  accept = "image/png,image/jpeg",
} = {}) {
  const inputRef = useRef(null);
  const [isBusy, setIsBusy] = useState(false);
  const [presentLoading, dismissLoading] = useIonLoading();

  // Both names may be given as a function so a caller can stamp each pick
  // (the post composer names its files with a timestamp).
  const resolveName = (value) => (typeof value === "function" ? value() : value);

  /**
   * Everything between the picker closing and the preview appearing.
   *
   * That gap is long — a full-size phone photo is decoded, drawn to a canvas at
   * its natural resolution and re-encoded as JPEG, which on a 12MP image runs
   * into tens of seconds on a mid-range device. With nothing on screen the
   * member sees the picker vanish and nothing happen, and reasonably concludes
   * the attachment failed. Hence a loader, owned here rather than in each
   * screen: every caller gets it, and it can never drift out of step with the
   * work it is reporting on.
   */
  const whilePreparing = useCallback(
    async (message, run) => {
      setIsBusy(true);
      await presentLoading({ message, spinner: "crescent" });
      try {
        return await run();
      } finally {
        await dismissLoading();
        setIsBusy(false);
      }
    },
    [presentLoading, dismissLoading],
  );

  const deliver = useCallback(
    async (file) => {
      if (!file) return;
      await onPicked?.(file);
    },
    [onPicked],
  );

  // Anything the member did not cause goes to onError; a cancel is silent.
  const guard = useCallback(
    async (run) => {
      try {
        await run();
      } catch (error) {
        if (isCancellation(error)) return;
        if (onError) onError(error);
      }
    },
    [onError],
  );

  const deliverPhotoFrom = useCallback(
    async (result) => {
      // Nothing to read means the member backed out — say nothing, show nothing.
      const src = mediaSrc(result);
      if (!src) return;
      await whilePreparing("Preparazione della foto…", async () => {
        await deliver(await imageUrlToJpegFile(src, resolveName(filename)));
      });
    },
    [deliver, filename, whilePreparing],
  );

  const pickFromCamera = useCallback(
    () =>
      guard(async () => {
        const result = await Camera.takePhoto({
          quality,
          correctOrientation: true,
        });
        await deliverPhotoFrom(result);
      }),
    [deliverPhotoFrom, guard, quality],
  );

  const pickFromGallery = useCallback(
    () =>
      guard(async () => {
        const results = await Camera.chooseFromGallery({
          mediaType: MediaTypeSelection.Photo,
          allowMultipleSelection: false,
          limit: 1,
          quality,
          correctOrientation: true,
        });
        await deliverPhotoFrom(firstResult(results));
      }),
    [deliverPhotoFrom, guard, quality],
  );

  /**
   * Native video pick. Callers must accept what the system gallery actually
   * returns — on iOS that is usually .mov, which both the comment and post
   * upload endpoints take (mimes:mp4,mov,m4v,webm). Android uses the file
   * chooser instead (see chooseVideoFile).
   */
  const pickVideoFromGallery = useCallback(
    () =>
      guard(async () => {
        if (Capacitor.getPlatform() === "android") {
          const file = await chooseVideoFile();
          if (!file) return;
          await whilePreparing("Preparazione del video…", async () => {
            await deliver(file);
          });
          return;
        }
        const results = await Camera.chooseFromGallery({
          mediaType: MediaTypeSelection.Video,
          allowMultipleSelection: false,
          limit: 1,
        });
        const src = mediaSrc(firstResult(results));
        if (!src) return;
        await whilePreparing("Preparazione del video…", async () => {
          await deliver(await fetchAsFile(src, resolveName(videoBaseName)));
        });
      }),
    [deliver, guard, videoBaseName, whilePreparing],
  );

  /**
   * Record a clip with the camera.
   *
   * `recordVideo` is not implemented on web, so the caller is expected to hide
   * the option there (see `canRecordVideo`) — but the guard below still catches
   * it rather than surfacing the plugin's raw "unimplemented" message.
   *
   * There is no duration option on `RecordVideoOptions`, so the cap is applied
   * after the fact by whoever asked for the clip. That is unavoidable with this
   * plugin and it is why the composer reads the duration before uploading.
   */
  const recordVideo = useCallback(
    () =>
      guard(async () => {
        if (!Capacitor.isNativePlatform()) {
          inputRef.current?.click();
          return;
        }
        const result = await Camera.recordVideo({
          saveToGallery: false,
          isPersistent: true,
        });
        const src = mediaSrc(result);
        if (!src) return;
        await whilePreparing("Preparazione del video…", async () => {
          await deliver(await fetchAsFile(src, resolveName(videoBaseName)));
        });
      }),
    [deliver, guard, videoBaseName, whilePreparing],
  );

  const openWebPicker = useCallback(() => inputRef.current?.click(), []);

  const onWebInputChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      // Reset first so choosing the same file twice still fires a change.
      event.target.value = "";
      if (!file) return;
      // Only the web input needs this. On the native path the plugin applies
      // correctOrientation and the canvas re-encode in imageUrlToJpegFile bakes
      // the result into the pixels, so a third pass would just cost quality.
      const isVideo = (file.type || "").startsWith("video/");
      await whilePreparing(
        isVideo ? "Preparazione del video…" : "Preparazione della foto…",
        async () => {
          const upright = isVideo
            ? file
            : await correctImageOrientation(file).catch(() => file);
          await deliver(upright);
        },
      );
    },
    [deliver, whilePreparing],
  );

  return {
    isNative: Capacitor.isNativePlatform(),
    isBusy,
    pickFromCamera,
    pickFromGallery,
    pickVideoFromGallery,
    recordVideo,
    // The plugin has no web implementation for recording, so a caller offering
    // a "record a video" button must hide it rather than let it throw.
    canRecordVideo: Capacitor.isNativePlatform(),
    openWebPicker,
    webInputProps: {
      ref: inputRef,
      type: "file",
      accept,
      style: { display: "none" },
      onChange: onWebInputChange,
    },
  };
}
